import { useState } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";

// Analytics tracking stub
function trackPurchaseClick(optionName: string) {
    if (window.analytics) {
        window.analytics.track("Purchase Click", { option: optionName });
    } else {
        console.log("Purchase Click:", optionName);
    }
}

type Option = {
    name: string;
    desc: string;
    price: string;
    url: string;
    premiumUrl: string;
};

const baseOptions: Option[] = [
    {
        name: "Standard Queue Match",
        desc: "Join optimized matches in 0–2 hours.",
        price: "$2.99",
        url: "https://buy.stripe.com/9AQ6qP5p41WQeD6eUV",
        premiumUrl: "https://buy.stripe.com/9AQ02r9FkcBu3Ys5km",
    },
    {
        name: "High Priority Queue Match",
        desc: "Get in 0–1 hour for faster action.",
        price: "$4.99",
        url: "https://buy.stripe.com/00g8yX8Bg44Y0Mg8wz",
        premiumUrl: "https://buy.stripe.com/14kbL99FkeJC2UoaEI",
    },
    {
        name: "Instant Queue Match",
        desc: "Jump straight in within 0–10 minutes.",
        price: "$9.99",
        url: "https://buy.stripe.com/00g5mLg3I592fHa6ot",
        premiumUrl: "https://buy.stripe.com/00g02reZEeJCeD614a",
    },
];

export default function BotLobbyPage() {
    return (
        <div className="min-h-screen w-full p-4 bg-gradient-to-br from-indigo-300 via-purple-300 to-pink-200 dark:bg-gray-900 text-gray-900 dark:text-white flex justify-center items-start py-10">
            <div className="w-full max-w-4xl p-6 bg-white/80 dark:bg-gray-800 backdrop-blur rounded-2xl shadow-xl">
                <h1 className="text-3xl font-extrabold mb-8 text-center">
                    Nebula Tactics Bot Lobby
                </h1>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {baseOptions.map((opt) => (
                        <OptionCard key={opt.name} option={opt} />
                    ))}
                    <SubscriptionCard />
                </div>
            </div>
        </div>
    );
}

type OptionCardProps = { option: Option };

function OptionCard({ option }: OptionCardProps) {
    const [showConfirm, setShowConfirm] = useState(false);

    const handleBuyClick = () => {
        setShowConfirm(true);
    };

    const handleConfirm = (usePremium: boolean) => {
        setShowConfirm(false);
        trackPurchaseClick(
            `${option.name}${usePremium ? " (Premium)" : ""}`
        );
        window.location.href = usePremium ? option.premiumUrl : option.url;
    };

    return (
        <>
            <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="relative"
            >
                <Card className="h-full bg-white/80 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-xl">
                    <CardHeader>
                        <CardTitle className="text-xl font-semibold">
                            {option.name}
                        </CardTitle>
                        <CardDescription className="text-sm text-gray-600 dark:text-gray-400">
                            {option.desc}
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="mt-4">
            <span className="text-2xl font-bold mb-1 block">
              {option.price}
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {" "}(+$15 for premium)
              </span>
            </span>
                        <Button
                            className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold shadow-lg hover:scale-105 transition-transform duration-200"
                            onClick={handleBuyClick}
                        >
                            Buy Now
                        </Button>
                    </CardContent>
                </Card>
            </motion.div>

            <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Select Standard or Premium</DialogTitle>
                    </DialogHeader>
                    <DialogDescription className="space-y-2">
                        <p>The lobby is completely yours—no other players, just you and bots.</p>
                        <p>Freedom to complete any challenges or just have fun (Nuke if you want!).</p>
                        <p>Up to 400 kills per game (15–20 minutes).</p>
                        <p>Queue time remains based on your selected queue priority.</p>
                    </DialogDescription>
                    <div className="flex justify-end space-x-2 pt-4">
                        <Button variant="outline" onClick={() => handleConfirm(false)}>
                            Standard
                        </Button>
                        <Button onClick={() => handleConfirm(true)}>
                            Premium
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}

function SubscriptionCard() {
    const name = "Weekly Premium Subscription";
    const price = "$69.99/week";
    const url = "https://buy.stripe.com/cN23eDg3I0SMgLe000";

    const handleSubscribe = () => {
        trackPurchaseClick(name);
        window.location.href = url;
    };

    return (
        <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="relative col-span-full sm:col-span-2"
        >
            <Card className="h-full bg-white/80 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-xl relative">
                <Badge className="absolute top-4 right-4 bg-yellow-300 text-black">
                    🔥 Recommended
                </Badge>
                <CardHeader>
                    <CardTitle className="text-xl font-semibold">
                        {name}
                    </CardTitle>
                    <CardDescription className="text-sm text-gray-600 dark:text-gray-400">
                        Unlimited premium matches, 24/7 instant access.
                    </CardDescription>
                </CardHeader>
                <CardContent className="mt-4 flex items-center justify-between p-4">
                    <span className="text-2xl font-bold">{price}</span>
                    <Button
                        className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold shadow-lg hover:scale-105 transition-transform duration-200"
                        onClick={handleSubscribe}
                    >
                        Subscribe
                    </Button>
                </CardContent>
            </Card>
        </motion.div>
    );
}
